import { zodResolver } from '@hookform/resolvers/zod';
import { EBatchType } from '@quesmed/types/models';
import {
  type IToggleVisibilityData,
  type IToggleVisibilityVar,
  TOGGLE_VISIBILITY,
} from '@quesmed/types/resolvers/mutation/admin';
import { motion } from 'framer-motion';
import { useEffect, useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import z from 'zod';
import { ErrorFeedback, Heading, SuccessFeedback } from '@/core/molecules';
import {
  Alert,
  AnimatedComponent,
  Button,
  cn,
  FormCheckBox,
  FormInput,
  FormSelect,
  FormTextArea,
} from '@/core/ui';
import { useMutation } from '@/libs';
import { stringToNumberArr } from '@/utils';

const contentTypes = [
  { key: EBatchType.QUESTION.toString(), label: EBatchType[EBatchType.QUESTION] },
  { key: EBatchType.STATION.toString(), label: EBatchType[EBatchType.STATION] },
  { key: EBatchType.CARD.toString(), label: EBatchType[EBatchType.CARD] },
  { key: EBatchType.CONCEPT.toString(), label: EBatchType[EBatchType.CONCEPT] },
  { key: EBatchType.TOPIC.toString(), label: EBatchType[EBatchType.TOPIC] },
  { key: EBatchType.MOCK_TEST.toString(), label: EBatchType[EBatchType.MOCK_TEST] },
];

const schema = z
  .object({
    isBatch: z.boolean().optional(),
    visibility: z.boolean(),
    batchId: z.uuid().optional(),
    type: z.string().optional(),
    ids: z
      .string()
      .regex(/^[\d\r\n\s,]+$/, 'Ids must only contain numbers, comma and whitespace')
      .optional(),
  })
  .superRefine((data, ctx) => {
    const { isBatch, batchId, type, ids } = data;
    if (isBatch) {
      if (!batchId) {
        ctx.addIssue({
          path: ['batchId'],
          code: 'custom',
          message: 'BatchId is required when batch mode is enabled',
        });
      }
    } else {
      if (!type) {
        ctx.addIssue({
          path: ['type'],
          code: 'custom',
          message: 'Type is required when batch mode is disabled',
        });
      }
      if (!ids || ids.length === 0) {
        ctx.addIssue({
          path: ['ids'],
          code: 'custom',
          message: 'IDs are required when batch mode is disabled',
        });
      }
    }
  });

type ISchema = z.infer<typeof schema>;

const BatchPublic = () => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      isBatch: false,
      visibility: true,
      ids: '',
      type: EBatchType.QUESTION.toString(),
    },
    shouldUnregister: true,
  });

  useEffect(() => {
    setValue('isBatch', true);
  }, []);

  const [toggleVisibility, { loading, data }] = useMutation<
    IToggleVisibilityData,
    { input: IToggleVisibilityVar }
  >(TOGGLE_VISIBILITY);

  const finalData = useMemo(() => data?.admin?.toggleVisibility, [data?.admin?.toggleVisibility]);

  const onSubmit: SubmitHandler<ISchema> = async (input) => {
    await toggleVisibility({
      variables: {
        input: {
          visibility: input.visibility,
          batchId: input.isBatch ? input.batchId : undefined,
          ids: input.isBatch && input.ids ? stringToNumberArr(input.ids) : [],
          type: input.type ? (Number(input.type) as EBatchType) : undefined,
        },
      },
    });
  };

  return (
    <div className='flex flex-col space-y-2 justify-center w-full items-center'>
      <div className='bg-background rounded-lg shadow-md w-full max-w-5xl z-[2]'>
        <Heading title='Modify Content Visibility' />

        <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-10'>
          <Alert
            message='Please read below before proceeding!'
            type='warn'
            className='mb-10'
            list={[
              'Please Make sure correct DB storage is selected!',
              'If data is still not visible, It maybe because data is cached, wait for a day or ask dev to clear cache',
            ]}
          />
          <div className='grid grid-cols-2 items-start gap-y-6 md:gap-y-10 gap-x-8'>
            <FormCheckBox
              classNames={{ label: 'text-sm' }}
              control={control}
              // defaultValue={true}
              name='isBatch'
              className='col-span-2'
            >
              <span className='text-foreground-500 text-small'>Batch Mode</span>
            </FormCheckBox>

            <FormSelect
              isRequired={true}
              description='Select visibility status'
              options={[
                { key: 'true', label: 'Visible' },
                { key: 'false', label: 'Hidden' },
              ]}
              control={control}
              transform={(a) => (a ? JSON.parse(a) : undefined)}
              labelPlacement='outside'
              name='visibility'
              placeholder='Select Visibility'
              className='rounded col-span-2 md:col-span-1'
              variant='faded'
              label='Visibility'
            />

            {watch('isBatch') ? (
              <FormInput
                isRequired={true}
                description='Enter the batchId'
                control={control}
                name='batchId'
                labelPlacement='inside'
                placeholder='04fe9191-24da-47bc-80d5-61f5089337ab'
                className='rounded col-span-2 md:col-span-1'
                variant='faded'
                label='Batch Id'
              />
            ) : (
              <>
                <FormSelect
                  isRequired={true}
                  description='Select the type'
                  options={contentTypes}
                  control={control}
                  labelPlacement='outside'
                  name='type'
                  placeholder='Select Type'
                  className='rounded col-span-2 md:col-span-1'
                  variant='faded'
                  label='Type'
                />
                <FormTextArea
                  isRequired={true}
                  description='Enter comma-separated IDs'
                  control={control}
                  name='ids'
                  labelPlacement='outside'
                  placeholder='1, 2, 3'
                  className='rounded col-span-2'
                  variant='faded'
                  label='IDs'
                />
              </>
            )}
          </div>
          <div className='flex justify-center mt-10'>
            <Button
              isDisabled={!isValid}
              isLoading={loading}
              className='w-full max-w-xl bg-primary'
              type='submit'
              radius='sm'
              size='lg'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
      <AnimatedComponent>
        {finalData && (
          <motion.div
            initial={{ y: -160 }}
            animate={{ y: 0 }}
            exit={{ y: -160 }}
            transition={{ type: 'spring' }}
            className={cn(
              'flex flex-col space-y-4 justify-center items-center',
              'w-full max-w-5xl py-5',
              'bg-background rounded-lg shadow-md ',
            )}
          >
            {finalData?.status === 200 ? (
              <SuccessFeedback showBatch={false} message={finalData?.message} response={finalData} />
            ) : (
              <ErrorFeedback response={finalData} />
            )}
          </motion.div>
        )}
      </AnimatedComponent>
    </div>
  );
};

export default BatchPublic;
